import { useState, useCallback } from "react";
import {
  Plus,
  Trash2,
  Loader2,
  Upload,
  Link,
  FileText,
} from "lucide-react";
import {
  Button,
  Input,
  Label,
} from "@b2b/ui-components";
import type { ProfileData, BusinessProfileExtended } from "../types/profile";
import { cn } from "../../../lib/utils";

const ACCEPT_LOGO = "image/jpeg,image/png,image/gif,image/svg+xml";
const ACCEPT_BROCHURE = "application/pdf,image/jpeg,image/png";
const ACCEPT_PDF_IMAGE = "application/pdf,image/jpeg,image/png";
const MAX_LOGO_MB = 2;
const MAX_BROCHURE_MB = 5;
const MAX_BROCHURE_COUNT = 5;

function brochurePathLabel(url: string, index: number): string {
  try {
    const path = url.split("?")[0] || url;
    const seg = path.split("/").filter(Boolean).pop();
    if (seg) return seg;
  } catch {
    /* ignore */
  }
  return `Brochure ${index + 1}`;
}

function isPdfBrochureUrl(url: string): boolean {
  const path = (url.split("?")[0] || "").toLowerCase();
  return path.endsWith(".pdf");
}

function BrochureThumbnail({
  url,
  brochureIndex,
  isEditing,
  onRemove,
}: {
  url: string;
  brochureIndex: number;
  isEditing: boolean;
  onRemove: () => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const label = brochurePathLabel(url, brochureIndex);
  const isPdf = isPdfBrochureUrl(url);
  const showFilePlaceholder = isPdf || imageFailed;

  return (
    <div className="relative flex w-[132px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        title={label}
      >
        {showFilePlaceholder ? (
          <div className="flex h-[104px] w-full flex-col items-center justify-center gap-1 bg-gray-100 px-2 text-center">
            <FileText className="h-9 w-9 shrink-0 text-gray-500" />
            <span className="line-clamp-2 text-[10px] leading-tight text-gray-600">
              {isPdf ? "PDF document" : "File"}
            </span>
          </div>
        ) : (
          <img
            src={url}
            alt={label}
            className="h-[104px] w-full bg-gray-50 object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        )}
      </a>
      <div className="flex min-h-[32px] items-center justify-between gap-1 border-t border-gray-100 px-1.5 py-1">
        <span className="flex-1 truncate text-[10px] text-gray-600" title={url}>
          {label}
        </span>
        {isEditing && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            className="shrink-0 rounded p-0.5 text-destructive hover:text-destructive/80"
            aria-label="Remove brochure"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

interface DragDropZoneProps {
  hint: string;
  accept: string;
  maxSizeMB?: number;
  value?: string | string[];
  multiple?: boolean;
  onFile: (file: File | File[]) => void;
  onClear?: () => void;
  isEditing: boolean;
  uploading?: boolean;
  uploadError?: string | null;
  className?: string;
}

export function DragDropZone({
  hint,
  accept,
  maxSizeMB = 5,
  value,
  multiple,
  onFile,
  onClear,
  isEditing,
  uploading,
  uploadError,
  className,
}: DragDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);

  const hasValue = Array.isArray(value)
    ? value.length > 0
    : typeof value === "string" && value.length > 0;

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!isEditing || uploading) return;
      const files = Array.from(e.dataTransfer.files);
      if (multiple && files.length > 0) onFile(files);
      else if (files.length > 0) onFile(files[0]);
    },
    [isEditing, multiple, onFile, uploading],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (multiple) onFile(Array.from(files));
    else onFile(files[0]);
    e.target.value = "";
  };

  if (uploading) {
    return (
      <div className="flex items-center justify-center w-full h-28 border-2 border-dashed rounded-lg border-primary/40 bg-primary/5">
        <div className="flex flex-col items-center gap-2 text-primary">
          <Loader2 className="w-7 h-7 animate-spin" />
          <p className="text-sm font-medium">Uploading to storage…</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); if (isEditing) setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
          isDragging ? "border-orange-400 bg-orange-50/50" : "border-gray-300 bg-gray-50/50",
          hasValue && "border-green-300 bg-green-50/30",
          uploadError && "border-destructive",
          className,
        )}
      >
        {hasValue && !multiple && typeof value === "string" && (
          <div className="mb-3 flex justify-center">
            {value.match(/\.(jpg|jpeg|png|gif|svg)/i) ? (
              <img
                src={value}
                alt="Preview"
                className="h-20 w-20 object-contain rounded border border-gray-200"
              />
            ) : (
              <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                {value.split("/").pop() || value}
              </span>
            )}
          </div>
        )}
        {hasValue && multiple && Array.isArray(value) && (
          <p className="text-sm text-muted-foreground mb-2">{value.length} file(s) uploaded</p>
        )}
        <p className="text-sm text-muted-foreground mb-2">
          {hint}
          {maxSizeMB && `, max ${maxSizeMB}MB`}
        </p>
        {/* <div className="flex flex-wrap items-center justify-center gap-2">
          <label className={cn("cursor-pointer", !isEditing && "pointer-events-none opacity-60")}>
            <input type="file" accept={accept} multiple={multiple} onChange={handleChange} className="hidden" disabled={!isEditing} />
            <span className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background px-4 py-2 hover:bg-accent hover:text-accent-foreground gap-1.5">
              <Upload className="h-3.5 w-3.5" />
              Choose File
            </span>
          </label>
          {hasValue && onClear && isEditing && (
            <Button type="button" variant="ghost" size="sm" onClick={onClear}>Clear</Button>
          )}
        </div> */}
        <div className="flex flex-wrap items-center justify-center gap-2">
  {/* Choose File */}
  <label className={cn("cursor-pointer", !isEditing && "pointer-events-none opacity-60")}>
    <input
      type="file"
      accept={accept}
      multiple={multiple}
      onChange={handleChange}
      className="hidden"
      disabled={!isEditing}
    />
    <span className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background px-4 py-2 hover:bg-accent hover:text-accent-foreground gap-1.5">
      <Upload className="h-3.5 w-3.5" />
      Choose File
    </span>
  </label>

  {/* ✅ NEW: Preview Button */}
  {hasValue && value && (
    <button
      type="button"
      onClick={() => {
        const previewUrl = Array.isArray(value) ? value[0] : value;
        if (previewUrl) window.open(previewUrl, "_blank");
      }}
      className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background px-4 py-2 hover:bg-accent hover:text-accent-foreground"
    >
      Preview
    </button>
  )}

  {/* Clear Button (UNCHANGED) */}
  {hasValue && onClear && isEditing && (
    <Button type="button" variant="ghost" size="sm" onClick={onClear}>
      Clear
    </Button>
  )}
</div>
      </div>
      {uploadError && <p className="text-xs text-destructive mt-1">{uploadError}</p>}
    </div>
  );
}

interface BrandingMediaSectionProps {
  formData: ProfileData;
  isEditing: boolean;
  onFieldChange: (fieldKey: string, value: string | File | boolean | string[]) => void;
  onExtendedChange: (extended: BusinessProfileExtended) => void;
  errors?: Record<string, string>;
  /** Upload a file to S3 and return the public URL */
  onUpload?: (file: File) => Promise<string>;
}

export function BrandingMediaSection({
  formData,
  isEditing,
  onFieldChange,
  onExtendedChange,
  errors = {},
  onUpload,
}: BrandingMediaSectionProps) {
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [brochureUploading, setBrochureUploading] = useState(false);
  const [brochureError, setBrochureError] = useState<string | null>(null);
  const [brandUploading, setBrandUploading] = useState<Record<number, boolean>>({});
  const [certUploading, setCertUploading] = useState<Record<number, boolean>>({});

  const companyInfo = formData.companyInfo || {};
  const extended = formData.businessProfileExtended || {};

  const companyLogo = typeof companyInfo.companyLogo?.value === "string" ? companyInfo.companyLogo.value : "";
  const companyWebsite = typeof companyInfo.companyWebsite?.value === "string" ? companyInfo.companyWebsite.value : "";
  const companyVideoUrl = typeof companyInfo.companyVideoUrl?.value === "string" ? companyInfo.companyVideoUrl.value : "";
  const companyBrochures: string[] = Array.isArray(companyInfo.companyBrochures?.value)
    ? (companyInfo.companyBrochures.value as unknown[]).filter((x): x is string => typeof x === "string")
    : extended.companyBrochures || [];
  const ownBrands = extended.ownBrands || [];
  const certificatesQuality = extended.certificatesQuality || [];

  const facebookUrl = typeof companyInfo.facebookUrl?.value === "string" ? companyInfo.facebookUrl.value : "";
  const youtubeUrl = typeof companyInfo.youtubeUrl?.value === "string" ? companyInfo.youtubeUrl.value : "";
  const instagramUrl = typeof companyInfo.instagramUrl?.value === "string" ? companyInfo.instagramUrl.value : "";
  const linkedinUrl = typeof companyInfo.linkedinUrl?.value === "string" ? companyInfo.linkedinUrl.value : "";

  const updateExtended = (patch: Partial<BusinessProfileExtended>) => {
    onExtendedChange({ ...extended, ...patch });
  };

  const handleLogoFile = async (file: File | File[]) => {
    const f = Array.isArray(file) ? file[0] : file;
    if (!f) return;
    setLogoError(null);
    const maxBytes = MAX_LOGO_MB * 1024 * 1024;
    if (f.size > maxBytes) {
      setLogoError(`File exceeds ${MAX_LOGO_MB}MB. Please choose an image up to ${MAX_LOGO_MB}MB.`);
      return;
    }
    if (onUpload) {
      setLogoUploading(true);
      try {
        const url = await onUpload(f);
        onFieldChange("companyLogo", url);
      } catch {
        setLogoError("Upload failed. Please try again.");
      } finally {
        setLogoUploading(false);
      }
    } else {
      onFieldChange("companyLogo", f);
    }
  };

  const handleBrochureFiles = async (files: File | File[]) => {
    const fileArr = Array.isArray(files) ? files : [files];
    setBrochureError(null);
    const remaining = MAX_BROCHURE_COUNT - companyBrochures.length;
    const toUpload = fileArr.slice(0, remaining);
    if (toUpload.length === 0) {
      setBrochureError(`Maximum ${MAX_BROCHURE_COUNT} brochures allowed.`);
      return;
    }
    if (onUpload) {
      setBrochureUploading(true);
      try {
        const urls = await Promise.all(toUpload.map((f) => onUpload(f)));
        onFieldChange("companyBrochures", [...companyBrochures, ...urls]);
      } catch {
        setBrochureError("Upload failed. Please try again.");
      } finally {
        setBrochureUploading(false);
      }
    } else {
      const urls = toUpload.map((f) => URL.createObjectURL(f));
      onFieldChange("companyBrochures", [...companyBrochures, ...urls].slice(0, MAX_BROCHURE_COUNT));
    }
  };

  const handleBrandFile = async (index: number, file: File) => {
    if (onUpload) {
      setBrandUploading((prev) => ({ ...prev, [index]: true }));
      try {
        const url = await onUpload(file);
        const next = [...ownBrands];
        next[index] = { ...next[index], fileUrl: url };
        updateExtended({ ownBrands: next });
      } catch {
        // silently ignore; user can retry
      } finally {
        setBrandUploading((prev) => ({ ...prev, [index]: false }));
      }
    } else {
      const next = [...ownBrands];
      next[index] = { ...next[index], fileUrl: URL.createObjectURL(file) };
      updateExtended({ ownBrands: next });
    }
  };

  const handleCertFile = async (index: number, file: File) => {
    if (onUpload) {
      setCertUploading((prev) => ({ ...prev, [index]: true }));
      try {
        const url = await onUpload(file);
        const next = [...certificatesQuality];
        next[index] = { ...next[index], fileUrl: url };
        updateExtended({ certificatesQuality: next });
      } catch {
        // silently ignore; user can retry
      } finally {
        setCertUploading((prev) => ({ ...prev, [index]: false }));
      }
    } else {
      const next = [...certificatesQuality];
      next[index] = { ...next[index], fileUrl: URL.createObjectURL(file) };
      updateExtended({ certificatesQuality: next });
    }
  };

  const addOwnBrand = () => updateExtended({ ownBrands: [...ownBrands, { title: "", fileUrl: "" }] });
  const updateOwnBrandTitle = (index: number, title: string) => {
    const next = [...ownBrands];
    next[index] = { ...next[index], title };
    updateExtended({ ownBrands: next });
  };
  const removeOwnBrand = (index: number) => updateExtended({ ownBrands: ownBrands.filter((_, i) => i !== index) });

  const addCertificate = () => updateExtended({ certificatesQuality: [...certificatesQuality, { title: "", fileUrl: "" }] });
  const updateCertTitle = (index: number, title: string) => {
    const next = [...certificatesQuality];
    next[index] = { ...next[index], title };
    updateExtended({ certificatesQuality: next });
  };
  const removeCertificate = (index: number) => updateExtended({ certificatesQuality: certificatesQuality.filter((_, i) => i !== index) });

  return (
    <div className="space-y-6">
      {/* Company Logo */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">
          Company Logo{" "}
          {isEditing && <span className="text-red-600">*</span>}
        </Label>
        <DragDropZone
          hint="Drag & drop or choose a file (JPG, PNG, GIF, SVG)"
          accept={ACCEPT_LOGO}
          maxSizeMB={MAX_LOGO_MB}
          value={companyLogo}
          onFile={handleLogoFile}
          onClear={() => onFieldChange("companyLogo", "")}
          isEditing={isEditing}
          uploading={logoUploading}
          uploadError={logoError}
        />
        {errors.companyLogo && <p className="text-xs text-destructive">{errors.companyLogo}</p>}
      </div>

      {/* Company Website */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <Link className="h-4 w-4 text-muted-foreground" />
          Website
        </Label>
        <Input
          placeholder="https://companywebsite.com"
          value={companyWebsite}
          onChange={(e) => onFieldChange("companyWebsite", e.target.value)}
          disabled={!isEditing}
          className="h-10 border-gray-300 rounded"
        />
        {companyWebsite && (
          <a
            href={companyWebsite.startsWith("http") ? companyWebsite : `https://${companyWebsite}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline"
          >
            Open link ↗
          </a>
        )}
      </div>

      {/* Company Video — URL only */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <Link className="h-4 w-4 text-muted-foreground" />
          Company Video URL
        </Label>
        <Input
          placeholder="https://youtube.com/watch?v=... or any video URL"
          value={companyVideoUrl}
          onChange={(e) => onFieldChange("companyVideoUrl", e.target.value)}
          disabled={!isEditing}
          className="h-10 border-gray-300 rounded"
        />
        <p className="text-xs text-muted-foreground">
          Paste a YouTube, Vimeo or direct video link. No video file upload.
        </p>
        {companyVideoUrl && (
          <a
            href={companyVideoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline"
          >
            Preview link ↗
          </a>
        )}
      </div>

      {/* Company Brochures */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">Company Brochures</Label>
        {companyBrochures.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-3">
            {companyBrochures.map((url, idx) => (
              <BrochureThumbnail
                key={`brochure-${idx}-${url.slice(-24)}`}
                url={url}
                brochureIndex={idx}
                isEditing={isEditing}
                onRemove={() =>
                  onFieldChange(
                    "companyBrochures",
                    companyBrochures.filter((_, i) => i !== idx),
                  )
                }
              />
            ))}
          </div>
        )}
        {isEditing && companyBrochures.length < MAX_BROCHURE_COUNT && (
          <DragDropZone
            hint={`Upload up to ${MAX_BROCHURE_COUNT} files (PDF, JPG, PNG)`}
            accept={ACCEPT_BROCHURE}
            maxSizeMB={MAX_BROCHURE_MB}
            value={[]}
            multiple
            onFile={handleBrochureFiles}
            isEditing={isEditing}
            uploading={brochureUploading}
            uploadError={brochureError}
          />
        )}
        {!isEditing && companyBrochures.length === 0 && (
          <p className="text-sm text-muted-foreground">No brochures uploaded</p>
        )}
      </div>

      {/* Own Brands */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">Own Brands</Label>
        {ownBrands.map((item, index) => (
          <div key={index} className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50/50">
            <Input
              placeholder="Enter Brand Title"
              value={item.title || ""}
              onChange={(e) => updateOwnBrandTitle(index, e.target.value)}
              className="h-10"
              disabled={!isEditing}
            />
            <DragDropZone
              hint="Upload brand file (PDF, JPG, PNG)"
              accept={ACCEPT_PDF_IMAGE}
              maxSizeMB={5}
              value={item.fileUrl}
              onFile={(f) => handleBrandFile(index, Array.isArray(f) ? f[0] : f)}
              onClear={() => {
                const next = [...ownBrands];
                next[index] = { ...next[index], fileUrl: "" };
                updateExtended({ ownBrands: next });
              }}
              isEditing={isEditing}
              uploading={brandUploading[index]}
            />
            {isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive self-end"
                onClick={() => removeOwnBrand(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        {isEditing && (
          <Button type="button" variant="outline" onClick={addOwnBrand} className="w-full border-dashed">
            <Plus className="h-4 w-4 mr-2" />
            Add Own Brand
          </Button>
        )}
      </div>

      {/* Certificates & Quality Standards */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">Certificates &amp; Quality Standards</Label>
        {certificatesQuality.map((item, index) => (
          <div key={index} className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50/50">
            <Input
              placeholder="Enter Certificate Title"
              value={item.title || ""}
              onChange={(e) => updateCertTitle(index, e.target.value)}
              className="h-10"
              disabled={!isEditing}
            />
            <DragDropZone
              hint="Upload certificate (PDF, JPG, PNG)"
              accept={ACCEPT_PDF_IMAGE}
              maxSizeMB={5}
              value={item.fileUrl}
              onFile={(f) => handleCertFile(index, Array.isArray(f) ? f[0] : f)}
              onClear={() => {
                const next = [...certificatesQuality];
                next[index] = { ...next[index], fileUrl: "" };
                updateExtended({ certificatesQuality: next });
              }}
              isEditing={isEditing}
              uploading={certUploading[index]}
            />
            {isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive self-end"
                onClick={() => removeCertificate(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        {isEditing && (
          <Button type="button" variant="outline" onClick={addCertificate} className="w-full border-dashed">
            <Plus className="h-4 w-4 mr-2" />
            Add Certificate
          </Button>
        )}
      </div>

      {/* Social Media Links */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700">Social Media Links</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-5 h-5 rounded bg-[#1877F2] flex items-center justify-center text-white text-[10px] font-bold">f</span>
              Facebook
            </Label>
            <Input placeholder="Enter Link" value={facebookUrl} onChange={(e) => onFieldChange("facebookUrl", e.target.value)} className="h-10" disabled={!isEditing} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-5 h-5 rounded bg-[#FF0000] flex items-center justify-center text-white text-[10px] font-bold">▶</span>
              YouTube
            </Label>
            <Input placeholder="Enter Link" value={youtubeUrl} onChange={(e) => onFieldChange("youtubeUrl", e.target.value)} className="h-10" disabled={!isEditing} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-5 h-5 rounded bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center text-white text-[10px] font-bold">📷</span>
              Instagram
            </Label>
            <Input placeholder="Enter Link" value={instagramUrl} onChange={(e) => onFieldChange("instagramUrl", e.target.value)} className="h-10" disabled={!isEditing} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-5 h-5 rounded bg-[#0A66C2] flex items-center justify-center text-white text-[10px] font-bold">in</span>
              LinkedIn
            </Label>
            <Input placeholder="Enter Link" value={linkedinUrl} onChange={(e) => onFieldChange("linkedinUrl", e.target.value)} className="h-10" disabled={!isEditing} />
          </div>
        </div>
      </div>
    </div>
  );
}
